import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipItem,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";
import { ChartBarIcon, ChevronUpDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { IStationOverTargetResult } from "../../../models/ProductionDashboard";
import DashboardTile from "../../dashboard/DashboardTile";
import LoadingWheel from "../LoadingWheel";
import { averageOverMinutes, buildOverTargetChart, lineKey, OTHER_LINES_LABEL, toMinutes } from "./overTargetChart";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface IStationOverTargetGraphProps {
  title: string;
  result?: IStationOverTargetResult;
  isLoading?: boolean;
  isError?: boolean;
  /** False when the picker has been cleared, so the empty state says "pick a range" not "nothing over". */
  hasRange?: boolean;
  /** Rendered before the graph's own controls - the date picker, when no other panel on the page carries it. */
  headerControls?: ReactNode;
}

/** Minutes to one decimal, but "35 min" rather than "35.0 min". */
function formatMinutes(minutes: number, t: TFunction): string {
  return t("{{minutes}} min", { minutes: Number.isInteger(minutes) ? minutes : minutes.toFixed(1) });
}

/**
 * Vehicles that went over the station's target (by any amount), stacked by dismantling line,
 * one bar per station. The minutes behind them - total and average per over vehicle - are on
 * hover rather than a separate mode.
 *
 * Stacked rather than grouped because the question is "which station keeps running over" before
 * it is "on which line": the bar height is the station's count, and the colours split it. The
 * legend doubles as the line filter so a single line can be read on its own.
 */
export default function StationOverTargetGraph({
  title,
  result,
  isLoading = false,
  isError = false,
  hasRange = true,
  headerControls,
}: IStationOverTargetGraphProps) {
  const { t } = useTranslation("reports");
  const lines = useMemo(() => result?.lines ?? [], [result]);
  const lineKeys = useMemo(() => lines.map(lineKey), [lines]);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [hideZero, setHideZero] = useState(false);

  // Default to every line, and re-select whenever the set of lines changes (a new range can
  // introduce a bay or the no-line bucket that the previous one did not have).
  useEffect(() => {
    setSelectedKeys(lineKeys);
  }, [lineKeys]);

  const chart = useMemo(
    () =>
      buildOverTargetChart(result, {
        hideZeroStations: hideZero,
        visibleKeys: selectedKeys.filter((key) => lineKeys.includes(key)),
      }),
    [result, hideZero, selectedKeys, lineKeys]
  );

  const data = useMemo(
    () => ({
      labels: chart.labels,
      datasets: chart.series.map((series) => ({
        // Line names come from the API; only the folded "Other lines" bucket is ours to translate.
        label: series.label === OTHER_LINES_LABEL ? t(OTHER_LINES_LABEL) : series.label,
        data: series.overCounts,
        backgroundColor: series.colour,
        // A hairline of surface between stacked segments so adjacent colours never touch.
        borderColor: "#ffffff",
        borderWidth: { top: 1, bottom: 0, left: 0, right: 0 },
        maxBarThickness: 64,
        // Carried for the tooltip; Chart.js ignores unknown dataset keys.
        vehicleCounts: series.vehicleCounts,
        overSeconds: series.overSeconds,
      })),
    }),
    [chart, t]
  );

  const options = useMemo<ChartOptions<"bar">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: chart.series.length > 1, position: "top", labels: { boxWidth: 12 } },
        title: { display: false },
        tooltip: {
          callbacks: {
            label: (item: TooltipItem<"bar">) => {
              const dataset = item.dataset as typeof data.datasets[number];
              const over = typeof item.raw === "number" ? item.raw : 0;
              const through = dataset.vehicleCounts?.[item.dataIndex] ?? 0;
              const seconds = dataset.overSeconds?.[item.dataIndex] ?? 0;
              return [
                t("{{label}}: {{over}} of {{through}} vehicles over", { label: item.dataset.label, over, through }),
                t("Total {{total}}, average {{average}} per vehicle over", {
                  total: formatMinutes(toMinutes(seconds), t),
                  average: formatMinutes(averageOverMinutes(seconds, over), t),
                }),
              ];
            },
            // The station across every shown line: the average is over all its over vehicles
            // together, not the mean of each line's average.
            footer: (items: TooltipItem<"bar">[]) => {
              if (items.length < 2) return "";
              const over = items.reduce((sum, item) => sum + (typeof item.raw === "number" ? item.raw : 0), 0);
              const seconds = items.reduce(
                (sum, item) => sum + ((item.dataset as typeof data.datasets[number]).overSeconds?.[item.dataIndex] ?? 0),
                0
              );
              return [
                t("All lines: {{over}} vehicles over", { over }),
                t("Total {{total}}, average {{average}} per vehicle over", {
                  total: formatMinutes(toMinutes(seconds), t),
                  average: formatMinutes(averageOverMinutes(seconds, over), t),
                }),
              ];
            },
          },
        },
      },
      scales: {
        x: { stacked: true, grid: { display: false } },
        y: {
          stacked: true,
          beginAtZero: true,
          // Whole vehicles only - no 0.5 gridline on a quiet day.
          ticks: { precision: 0 },
          title: { display: true, text: t("Vehicles over target") },
          grid: { display: false },
        },
      },
    }),
    [chart.series.length, data, t]
  );

  const selectionLabel =
    selectedKeys.length === 0
      ? t("No lines")
      : selectedKeys.length === lineKeys.length
      ? t("All lines")
      : t("{{count}} lines", { count: selectedKeys.length });

  return (
    <DashboardTile span={2}>
      <div className="p-3">
        <div className="mb-3 flex w-full items-start justify-between gap-4">
          <div className="prose min-w-0">
            <h3 className="mb-0 flex items-center">
              <ChartBarIcon className="inline-block w-6 h-6 mr-2 text-gray-500" />
              {title}
            </h3>
            <p className="mt-0.5 mb-0 text-sm text-gray-500">
              {t("Vehicles that ran past each station's target in the chosen range, by any amount, split by dismantling line. Hover a bar for the total and average minutes over. Paused time is excluded.")}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            {headerControls}

            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={hideZero}
                onChange={(e) => setHideZero(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {t("Hide zeroes")}
            </label>

            <Listbox value={selectedKeys} onChange={setSelectedKeys} multiple>
              <div className="relative w-56">
                <ListboxButton className="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500">
                  <span className="block truncate">{selectionLabel}</span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-700" aria-hidden="true" />
                  </span>
                </ListboxButton>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute right-0 z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {lines.length === 0 ? (
                      <div className="px-4 py-2 text-gray-400">{t("No lines")}</div>
                    ) : (
                      lines.map((line) => (
                        <ListboxOption
                          key={lineKey(line)}
                          value={lineKey(line)}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                              active ? "bg-primary-100 text-gray-900" : "text-gray-900"
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}>
                                {line.name}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-primary-600">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </ListboxOption>
                      ))
                    )}
                  </ListboxOptions>
                </Transition>
              </div>
            </Listbox>
          </div>
        </div>

        {!hasRange ? (
          <div className="mt-6 py-12 text-center text-sm text-gray-500">
            {t("Choose a date range to see time over target.")}
          </div>
        ) : isLoading ? (
          <div className="mt-6 min-h-[200px]">
            <LoadingWheel />
          </div>
        ) : isError && !result ? (
          <div className="mt-6 py-12 text-center text-sm text-gray-500">
            {t("Could not load time over target. Try again in a moment.")}
          </div>
        ) : chart.stations.length === 0 ? (
          <div className="mt-6 py-12 text-center text-sm text-gray-500">
            {hideZero && (result?.stations.length ?? 0) > 0
              ? t("No station ran over target in this range.")
              : t("No stations configured.")}
          </div>
        ) : chart.series.length === 0 ? (
          <div className="mt-6 py-12 text-center text-sm text-gray-500">{t("No lines selected.")}</div>
        ) : (
          <>
            <div className="mt-6 h-96">
              <Bar options={options} data={data} />
            </div>
            {chart.isEmpty && (
              <p className="mt-3 text-center text-sm text-gray-500">
                {t("Nothing ran over target in this range.")}
              </p>
            )}
            {chart.untargeted.length > 0 && (
              <p className="mt-3 text-xs text-gray-500">
                {chart.untargeted.length === 1
                  ? t("No target is set for {{stations}}, so it cannot show time over. Targets are the average assembly time on each assembly.", { stations: chart.untargeted.join(", ") })
                  : t("No target is set for {{stations}}, so they cannot show time over. Targets are the average assembly time on each assembly.", { stations: chart.untargeted.join(", ") })}
              </p>
            )}
          </>
        )}
      </div>
    </DashboardTile>
  );
}
